import { useState } from "react";
import axios from "axios";
import { Video, ExternalLink, Edit3, X, Save, Trash2, Loader2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * RailCam card + admin/mod edit dialog.
 *   • If depot.has_railcam is true, shows a thumbnail-linked card.
 *   • If the current user is admin/mod, shows a pencil icon to edit, and (when
 *     there's no cam) an "Add RailCam" empty-state card.
 *   • Clearing the toggle also nulls the URL/thumbnail so a re-enable can't
 *     resurrect a dead link.
 */
export function RailCamCard({ depot, canEdit, onChange }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    has_railcam: !!depot.has_railcam,
    railcam_url: depot.railcam_url || "",
    railcam_thumbnail_url: depot.railcam_thumbnail_url || "",
    railcam_host_name: depot.railcam_host_name || "",
  });

  const openDialog = () => {
    setForm({
      has_railcam: !!depot.has_railcam,
      railcam_url: depot.railcam_url || "",
      railcam_thumbnail_url: depot.railcam_thumbnail_url || "",
      railcam_host_name: depot.railcam_host_name || "",
    });
    setOpen(true);
  };

  const save = async () => {
    if (form.has_railcam && !form.railcam_url.trim()) {
      toast.error("Please add a link to the live feed");
      return;
    }
    try {
      setSaving(true);
      const payload = {
        has_railcam: form.has_railcam,
        railcam_url: form.has_railcam ? form.railcam_url.trim() : null,
        railcam_thumbnail_url: form.has_railcam ? form.railcam_thumbnail_url.trim() || null : null,
        railcam_host_name: form.has_railcam ? form.railcam_host_name.trim() || null : null,
      };
      const r = await axios.patch(`${API}/admin/depots/${depot.id}`, payload, {
        withCredentials: true,
      });
      toast.success(form.has_railcam ? "RailCam saved" : "RailCam removed");
      setOpen(false);
      onChange?.(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const quickRemove = async () => {
    try {
      setSaving(true);
      const r = await axios.patch(
        `${API}/admin/depots/${depot.id}`,
        { has_railcam: false },
        { withCredentials: true }
      );
      toast.success("RailCam removed");
      onChange?.(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to remove");
    } finally {
      setSaving(false);
    }
  };

  const isActive = depot.has_railcam && depot.railcam_url;

  // No cam & no edit rights → render nothing.
  if (!isActive && !canEdit) return null;

  return (
    <>
      {isActive ? (
        <Card className="bg-[#111] border-white/5 overflow-hidden" data-testid="railcam-card">
          <CardContent className="p-0">
            <a
              href={depot.railcam_url}
              target="_blank"
              rel="noopener noreferrer"
              className="block relative group"
              data-testid="railcam-link"
            >
              {depot.railcam_thumbnail_url ? (
                <img
                  src={depot.railcam_thumbnail_url}
                  alt="Live railcam thumbnail"
                  className="w-full aspect-video object-cover group-hover:brightness-110 transition-all"
                  loading="lazy"
                  onError={(e) => {
                    // Broken/hotlink-blocked thumbnail → replace with the gradient/icon placeholder.
                    e.currentTarget.style.display = "none";
                    const fallback = e.currentTarget.nextElementSibling;
                    if (fallback) fallback.style.display = "flex";
                  }}
                />
              ) : null}
              <div
                className="w-full aspect-video bg-gradient-to-br from-red-900/40 via-black to-black items-center justify-center"
                style={{ display: depot.railcam_thumbnail_url ? "none" : "flex" }}
                aria-hidden="true"
              >
                <Video className="w-12 h-12 text-red-400/60" />
              </div>
              {/* LIVE badge */}
              <div className="absolute top-3 left-3 flex items-center gap-1.5 px-2 py-1 rounded-full bg-red-500/95 text-white text-[10px] uppercase tracking-[0.18em] font-bold shadow-lg">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-80" />
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-white" />
                </span>
                Live
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-3 flex items-end justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-white text-sm font-semibold flex items-center gap-1.5">
                    <Video className="w-3.5 h-3.5 text-red-400" />
                    Watch Live RailCam
                  </p>
                  {depot.railcam_host_name && (
                    <p className="text-[11px] text-white/70 mt-0.5 truncate">
                      hosted by {depot.railcam_host_name}
                    </p>
                  )}
                </div>
                <span className="text-xs text-white/85 inline-flex items-center gap-1 shrink-0 group-hover:text-amber-300 transition-colors">
                  Open
                  <ExternalLink className="w-3 h-3" />
                </span>
              </div>
            </a>
            {canEdit && (
              <div className="flex items-center justify-between px-3 py-2 bg-black/40 border-t border-white/5">
                <span className="text-[10px] uppercase tracking-[0.2em] text-amber-500/80 font-mono">
                  Staff tools
                </span>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={openDialog}
                    className="h-7 text-white/70 hover:text-amber-500 hover:bg-white/5 px-2"
                    data-testid="railcam-edit-btn"
                  >
                    <Edit3 className="w-3.5 h-3.5 mr-1" />
                    Edit
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={quickRemove}
                    disabled={saving}
                    className="h-7 text-white/60 hover:text-red-400 hover:bg-white/5 px-2"
                    data-testid="railcam-remove-btn"
                  >
                    <Trash2 className="w-3.5 h-3.5 mr-1" />
                    Remove
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        // Empty-state (admins only) — invites adding a cam.
        <Card className="bg-[#111] border-dashed border-white/10 hover:border-amber-500/40 transition-colors">
          <CardContent className="p-4">
            <button
              type="button"
              onClick={openDialog}
              className="w-full flex items-center gap-3 text-left group"
              data-testid="railcam-add-btn"
            >
              <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center shrink-0 group-hover:bg-red-500/20 transition-colors">
                <Video className="w-4 h-4 text-red-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-white font-medium">Add Live RailCam</p>
                <p className="text-xs text-white/75 mt-1 leading-relaxed">
                  Link a nearby webcam feed so railfans can watch trains pass by
                </p>
              </div>
              <Edit3 className="w-4 h-4 text-white/40 group-hover:text-amber-500 shrink-0" />
            </button>
          </CardContent>
        </Card>
      )}

      {/* Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md bg-[#1a1a1a] border-white/10" data-testid="railcam-dialog">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Video className="w-4 h-4 text-red-400" />
              Live RailCam
            </DialogTitle>
            <DialogDescription className="text-white/60">
              Point to a public live feed. Toggle off if the cam is gone — the URL
              is automatically cleared so it can&apos;t be re-enabled by accident.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="flex items-center justify-between bg-white/[0.03] rounded-md px-3 py-2.5 border border-white/5">
              <div>
                <Label htmlFor="railcam-toggle" className="text-sm text-white cursor-pointer">
                  Has Live RailCam
                </Label>
                <p className="text-xs text-white/40 mt-0.5">Turn on when a cam is available at or near this depot.</p>
              </div>
              <Switch
                id="railcam-toggle"
                checked={form.has_railcam}
                onCheckedChange={(v) => setForm((f) => ({ ...f, has_railcam: !!v }))}
                data-testid="railcam-toggle"
              />
            </div>

            <div className={form.has_railcam ? "" : "opacity-40 pointer-events-none"}>
              <Label className="text-xs text-white/70">Live feed URL *</Label>
              <Input
                value={form.railcam_url}
                onChange={(e) => setForm((f) => ({ ...f, railcam_url: e.target.value }))}
                placeholder="https://www.virtualrailfan.com/durand-mi"
                className="mt-1.5 bg-white/5 border-white/10 text-white placeholder:text-white/30"
                data-testid="railcam-url-input"
              />

              <Label className="text-xs text-white/70 mt-4 block">Thumbnail image URL (optional)</Label>
              <Input
                value={form.railcam_thumbnail_url}
                onChange={(e) => setForm((f) => ({ ...f, railcam_thumbnail_url: e.target.value }))}
                placeholder="https://.../thumb.jpg"
                className="mt-1.5 bg-white/5 border-white/10 text-white placeholder:text-white/30"
                data-testid="railcam-thumb-input"
              />

              <Label className="text-xs text-white/70 mt-4 block">Host / channel name (optional)</Label>
              <Input
                value={form.railcam_host_name}
                onChange={(e) => setForm((f) => ({ ...f, railcam_host_name: e.target.value }))}
                placeholder="Virtual Railfan · YouTube · etc."
                className="mt-1.5 bg-white/5 border-white/10 text-white placeholder:text-white/30"
                data-testid="railcam-host-input"
              />
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="ghost"
              onClick={() => setOpen(false)}
              className="text-white/70 hover:bg-white/5"
            >
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
            <Button
              onClick={save}
              disabled={saving}
              className="bg-amber-500 hover:bg-amber-600 text-black"
              data-testid="railcam-save-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving…
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
